import type { MeDto } from '@posilka/shared';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { useMe } from '@posilka/client/api/me';
import { useMyOrders } from '@posilka/client/api/orders';
import { Avatar } from '@/components/Avatar';
import { Icon } from '@/components/Icon';
import { OrderCard } from '@/components/OrderCard';
import { LinkRow } from '@/components/Section';
import { Skeleton } from '@/components/Skeleton';
import { EmptyState, ErrorState, ListSkeleton } from '@/components/States';
import { DriverHome } from '@/features/driver/DriverHome';
import { PhoneBanner } from '@/features/PhoneBanner';
import { haptic } from '@/lib/telegram';
import { useAppStore } from '@/lib/store';

function SenderHome() {
  const { t } = useTranslation();
  const orders = useMyOrders('active');
  const items = orders.data?.pages[0]?.items ?? [];

  return (
    <>
      <div className="mb-2 flex items-center justify-between px-2">
        <h2 className="text-xs font-bold tracking-wider text-muted uppercase">
          {t('home.activeOrders')}
          {items.length > 0 && <span className="ml-1.5 text-accent">{items.length}</span>}
        </h2>
        {items.length > 0 && (
          <Link to="/orders" className="text-sm font-semibold text-link">
            {t('common.all')} →
          </Link>
        )}
      </div>
      {orders.isPending ? (
        <ListSkeleton rows={2} />
      ) : orders.isError ? (
        <ErrorState error={orders.error} onRetry={() => void orders.refetch()} />
      ) : items.length === 0 ? (
        <div className="card mb-4">
          <EmptyState text={t('home.noActiveOrders')} />
        </div>
      ) : (
        items.slice(0, 5).map((o) => <OrderCard key={o.id} order={o} to={`/orders/${o.id}`} />)
      )}

      <div className="card mt-2 overflow-hidden">
        <LinkRow to="/rules" icon={<Icon name="info" className="h-5 w-5 text-muted" />}>
          {t('rules.link')}
        </LinkRow>
      </div>
    </>
  );
}

/** Hero: salomlashuv + asosiy amal (yuboruvchi — jo'natma yuborish). */
function Hero({ me, hasPhone }: { me: MeDto; hasPhone: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const mode = useAppStore((s) => s.mode);
  return (
    <div className="card glow mb-6 animate-fade-up p-5">
      <div className="flex items-center gap-3.5">
        <Avatar firstName={me.firstName} lastName={me.lastName} />
        <div className="min-w-0">
          <h1 className="font-display text-xl leading-tight font-bold tracking-tight">
            {t('home.greeting', { name: me.firstName })}
          </h1>
          {mode === 'sender' && <p className="mt-1 text-sm text-muted">{t('home.subtitle')}</p>}
        </div>
      </div>
      {mode === 'sender' && (
        <button
          type="button"
          disabled={!hasPhone}
          onClick={() => {
            haptic.tap();
            void navigate('/orders/new');
          }}
          className="btn-primary mt-5 w-full"
        >
          <Icon name="plus" className="h-5 w-5" strokeWidth={2.4} />
          {t('home.sendParcel')}
        </button>
      )}
    </div>
  );
}

export function HomePage() {
  const mode = useAppStore((s) => s.mode);
  const { data: me } = useMe();

  if (!me) return <Skeleton className="h-40" />;

  return (
    <>
      <Hero me={me} hasPhone={!!me.phone} />
      {!me.phone && <PhoneBanner />}
      {mode === 'sender' ? <SenderHome /> : <DriverHome me={me} />}
    </>
  );
}
